import { useCallback, useReducer } from 'react';

// Pure reducer: undo stack records committed items; "clear" stores a snapshot so it is undoable.
// WB-TEXT-EDIT-001 (Sep 2026): "update" (edit / move / recolor / resize) and "remove" entries for text objects.
const initial = { items: [], undoStack: [], redoStack: [] };

const applyUndo = (items, entry) => {
  if (entry.kind === 'add') return items.slice(0, -1);
  if (entry.kind === 'clear') return entry.snapshot;
  if (entry.kind === 'update') return items.map((it, i) => (i === entry.index ? entry.before : it));
  if (entry.kind === 'remove') return [...items.slice(0, entry.index), entry.item, ...items.slice(entry.index)];
  if (entry.kind === 'replace') {
    const out = items.filter((_, i) => i !== entry.insertAt);
    entry.indices.forEach((idx, k) => out.splice(idx, 0, entry.before[k]));
    return out;
  }
  return items;
};

const applyReplace = (items, indices, item, insertAt) => {
  const set = new Set(indices);
  const out = items.filter((_, i) => !set.has(i));
  out.splice(insertAt, 0, item);
  return out;
};

const applyRedo = (items, entry) => {
  if (entry.kind === 'add') return [...items, entry.item];
  if (entry.kind === 'clear') return [];
  if (entry.kind === 'update') return items.map((it, i) => (i === entry.index ? entry.after : it));
  if (entry.kind === 'remove') return items.filter((_, i) => i !== entry.index);
  if (entry.kind === 'replace') return applyReplace(items, entry.indices, entry.after, entry.insertAt);
  return items;
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'push':
      return {
        items: [...state.items, action.item],
        undoStack: [...state.undoStack, { kind: 'add', item: action.item }],
        redoStack: [],
      };
    case 'clear':
      if (!state.items.length) return state;
      return {
        items: [],
        undoStack: [...state.undoStack, { kind: 'clear', snapshot: state.items }],
        redoStack: [],
      };
    case 'update': {
      const before = state.items[action.index];
      if (!before) return state;
      return {
        items: state.items.map((it, i) => (i === action.index ? action.item : it)),
        undoStack: [...state.undoStack, { kind: 'update', index: action.index, before, after: action.item }],
        redoStack: [],
      };
    }
    case 'remove': {
      const item = state.items[action.index];
      if (!item) return state;
      return {
        items: state.items.filter((_, i) => i !== action.index),
        undoStack: [...state.undoStack, { kind: 'remove', index: action.index, item }],
        redoStack: [],
      };
    }
    // WB-INK-001: atomically swap N items (handwriting strokes) for one text item; one undo step restores the ink.
    case 'replace': {
      const indices = [...new Set(action.indices)].sort((a, b) => a - b);
      const before = indices.map((i) => state.items[i]);
      if (!before.length || before.some((it) => !it)) return state;
      const insertAt = indices[0];
      return {
        items: applyReplace(state.items, indices, action.item, insertAt),
        undoStack: [...state.undoStack, { kind: 'replace', indices, before, after: action.item, insertAt }],
        redoStack: [],
      };
    }
    case 'undo': {
      if (!state.undoStack.length) return state;
      const entry = state.undoStack[state.undoStack.length - 1];
      return {
        items: applyUndo(state.items, entry),
        undoStack: state.undoStack.slice(0, -1),
        redoStack: [...state.redoStack, entry],
      };
    }
    case 'redo': {
      if (!state.redoStack.length) return state;
      const entry = state.redoStack[state.redoStack.length - 1];
      return {
        items: applyRedo(state.items, entry),
        undoStack: [...state.undoStack, entry],
        redoStack: state.redoStack.slice(0, -1),
      };
    }
    case 'hydrate':
      return {
        items: action.items,
        undoStack: action.items.map((item) => ({ kind: 'add', item })),
        redoStack: [],
      };
    case 'reset':
      return initial;
    default:
      return state;
  }
};

export const whiteboardReducer = reducer;

export const useWhiteboardHistory = () => {
  const [state, dispatch] = useReducer(reducer, initial);
  const push = useCallback((item) => dispatch({ type: 'push', item }), []);
  const update = useCallback((index, item) => dispatch({ type: 'update', index, item }), []);
  const remove = useCallback((index) => dispatch({ type: 'remove', index }), []);
  const replace = useCallback((indices, item) => dispatch({ type: 'replace', indices, item }), []);
  const clear = useCallback(() => dispatch({ type: 'clear' }), []);
  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);
  const hydrate = useCallback((items) => dispatch({ type: 'hydrate', items }), []);
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);
  return {
    items: state.items,
    push,
    update,
    remove,
    replace,
    clear,
    undo,
    redo,
    hydrate,
    reset,
    canUndo: state.undoStack.length > 0,
    canRedo: state.redoStack.length > 0,
  };
};
